import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { Chart as ChartJS } from 'chart.js';
import Cookies from 'js-cookie';
import { withAdminLayout, withDashboardLayout } from './hoc/withLayout';

// Import components
import SignUp from "./components/Register/SignUp";
import Activate_Account from "./components/Register/Activate_Account";
import PassRedirect from "./components/Register/passredirect";
import Activate_SecAccount from "./components/Register/Activate_SecAccount";
import LogIn from "./components/Login/login";
import HomePage from "./components/Homepage/homepage";
import Affiliate_Url_Base from "./components/Affiliate_Url/Affiliate_Url.jsx";
import SummaryPageBase from "./components/Summary/Summary";
import ConsumerSummaryBase from "./components/Consumer/Summary.jsx";
import ConsumerShareCardBase from "./components/Consumer/ShareCard.jsx";
import ConsumerWisePaymentBase from "./components/Consumer/WisePayment.jsx";
import ConsumerLoginPage from "./components/Consumer/ConsumerLogin.jsx";
import ConsumerAccountInfoBase from "./components/Account_Info/Account_Info.jsx";
import ConsumerFaqPageBase from "./components/Consumer/FaqPage.jsx";
import ForgetPassword from './components/ForgotPassword/ForgotPassword';
import ResetPassword from './components/ResetPassword/ResetPassword';
import RuserSetup from './components/RuserSetup/SetUsername.jsx';
import OuserSetup from './components/RuserSetup/OSetUsername';
import TermsPage from './components/terms/TermsPage.jsx';
import CompliancePage from './components/compliance/CompliancePage.jsx';
import ErrorPage from './components/404-error/404';
import ComingSoon from './components/ComingSoon';
import { userService } from "./_services";
import { useUIStore } from './stores';
import packageJson from '../package.json';

// Admin pages - now with modern layouts
import GroupnoPageBase from './components/Admin/Group_no/GroupnoPage';
import TransactionsBase from './components/widget/admin/Transactions';
import UserTransactionsBase from './components/widget/user/Transactions';
import AnalyticsBase from './components/widget/admin/AnalyticsPage';
import GeographicBase from './components/widget/admin/Geographic';
import PharmaciesBase from './components/widget/admin/Pharmacies';
import UserPageBase from './components/Admin/Users/UsersPage';
import ReferrallinkPageBase from './components/Admin/Refferal_Link/Refferallink_Page';
import PricingPageBase from './components/Admin/Pricing_tool/PricingPage';
import LinksPageBase from './components/Admin/Secondary_Links/LinksPage';
import AdminTrainingBase from './components/Admin/Training/AdminTraining';
import ReportPageBase from './components/Admin/Reports/ReportPage';
import CommissionTrackingBase from './components/Admin/Reports/CommissionTracking';

// User pages - wrap with DashboardLayout
import AccountInfoPageBase from './components/Account_Info/Account_Info.jsx';
import OrderCardsPageBase from './components/Order_Cards/OrderCardsPage';
import FaqPageBase from './components/FAQ_s/FaqPage.jsx';
import UserTrainingBase from './components/TrainingPage/Training';
import EcommerceBase from './components/ecommerce/ecommerce';

// Consumer pages - keeping disconnected for now
// import ConsumerSummaryPage from "./components/Consumer/Summary";
// import ConsumerOrderCardsPage from './components/Consumer/ShareCard';
// import ConsumerLogIn from "./components/Consumer/ConsumerLogin";
// import ConsumerFaqPage from './components/Consumer/FaqPage';
import ConsumerTrainingBase from './components/Consumer/Training/Training';
// import Consumer_Account_Info from './components/Consumer/Account_Info/Account_Info';

// Specialized pages - keeping disconnected for now
// import Ecommerce from './components/ecommerce/ecommerce';
// import TimifitPage from './components/timifit/Timifit';

// CSS imports moved to index.js for correct load order (Tailwind must load last)

// Wrap admin pages with AdminLayout
const Groupno_Page = withAdminLayout(GroupnoPageBase);
const Transactions = withAdminLayout(TransactionsBase);
const Analytics = withAdminLayout(AnalyticsBase);
const Geographic = withAdminLayout(GeographicBase);
const Pharmacies = withAdminLayout(PharmaciesBase);
const User_Page = withAdminLayout(UserPageBase);
const Refferallink_Page = withAdminLayout(ReferrallinkPageBase);
const PricingPage = withAdminLayout(PricingPageBase);
const Links_Page = withAdminLayout(LinksPageBase);
const AdminTraining = withAdminLayout(AdminTrainingBase);
const ReportPage = withAdminLayout(ReportPageBase);
const CommissionTracking = withAdminLayout(CommissionTrackingBase);

const Account_Info_page = withDashboardLayout(AccountInfoPageBase);
const OrderCardsPage = withDashboardLayout(OrderCardsPageBase);
const FaqPage = withDashboardLayout(FaqPageBase);
const UserTraining = withDashboardLayout(UserTrainingBase);
const ConsumerTraining = withDashboardLayout(ConsumerTrainingBase);
const UserTransactions = withDashboardLayout(UserTransactionsBase);
const Ecommerce = withDashboardLayout(EcommerceBase);

// Wrap user pages with DashboardLayout
const Affiliate_Url = withDashboardLayout(Affiliate_Url_Base);
const SummaryPage = withDashboardLayout(SummaryPageBase);
const ConsumerSummary = withDashboardLayout(ConsumerSummaryBase);
const ConsumerShareCard = withDashboardLayout(ConsumerShareCardBase);
const ConsumerWisePayment = withDashboardLayout(ConsumerWisePaymentBase);
const ConsumerAccountInfo = withDashboardLayout(ConsumerAccountInfoBase);
const ConsumerFaqPage = withDashboardLayout(ConsumerFaqPageBase);

// Private Route component using React Router v6 pattern
const PrivateRoute = ({ children }) => {
  const isAuthenticated = !userService.Check_Null_Empty(localStorage.getItem("ajs_user_id"));
  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setSidebarOpen, setMobileMenuOpen, setActionMenuOpen } = useUIStore();

  useEffect(() => {
    GetDeploymentVersion();
    
    // Configure Chart.js defaults
    try {
      ChartJS.defaults.font.family = 'Urbanist';
    } catch (error) {
      console.error('ChartJS configuration error:', error);
    }

    // Handle referral URL validation
    try {
      const currentUrl = window.location.href;
      let match = currentUrl.match(/https:\/\/(localhost:44312|backoffice\.nulifespanrx\.com)\/ref\/([^\/]+)/);
      if (match) {
        let extracted = match[2];
        checkValidateRefLink(extracted);
      } else {
        let savedurl = localStorage.getItem('ReferralURL');
        if (savedurl) {
          let match1 = savedurl.match(/https:\/\/(localhost:44312|backoffice\.nulifespanrx\.com)\/ref\/([^\/]+)/);
          if (match1) {
            let extracted1 = match1[2];
            checkValidateRefLink(extracted1);
          }
        }
      }
    } catch (error) {
      console.error('Referral URL validation error:', error);
    }

    // Handle version caching
    if (!window.location.href.includes('/Account/Activate?uid=')) {
      handleCaching();
    }

    // Handle sidebar toggles using React state instead of jQuery
    const handleMenuClick = () => setSidebarOpen(true);
    const handleCloseClick = () => setSidebarOpen(false);
    const handleMobileMenuClick = (e) => {
      e.stopPropagation();
      setActionMenuOpen(true);
    };
    const handleWindowClick = () => {
      setTimeout(() => setActionMenuOpen(false), 300);
    };

    // Add event listeners (temporary during migration)
    document.addEventListener('click', handleWindowClick);

    // Handle timifit redirect
    if (window.location.href.includes('/timifit')) {
      localStorage.setItem('pagename', 'fit-economy');
      navigate('/fit-economy');
    }

    // Check user authentication and deactivation
    checkUserDeactivation();

    return () => {
      document.removeEventListener('click', handleWindowClick);
    };
  }, []);

  useEffect(() => {
    // Handle URL-specific logic on route change
    const currentUrl = window.location.href;
    let pathStr = location.pathname;
    pathStr = pathStr.charAt(pathStr.length - 1) === '/' ? pathStr.substr(0, pathStr.length - 1) : pathStr;
    
    if (pathStr !== '/signup') {
      window.localStorage.setItem('cp_pat_bo', pathStr);
    }

    // Handle referral URLs
    if (currentUrl.includes('nulifespanrx.com/ref/') || currentUrl.includes('/ref')) {
      localStorage.setItem('ReferralURL', currentUrl);
      try {
        var parts = window.location.hostname.split('.');
        var sndleveldomain = parts.slice(-2).join('.');
        Cookies.set('mainframe_url_clb', currentUrl, { 
          domain: sndleveldomain, 
          expires: 7, 
          secure: true, 
          sameSite: 'None', 
          path: '/' 
        });
      } catch (error) {
        console.error('Cookie setting error:', error);
      }
    }

    // Handle affiliate URLs
    if (currentUrl.includes('/ref?sign_completed') || currentUrl.includes('/affiliate?sign_completed')) {
      localStorage.setItem('AffiliateReferralURL', currentUrl);
    }

    if (currentUrl.includes('/affiliate')) {
      localStorage.setItem('ReferralURL', currentUrl);
    }

    // Handle signup referral
    if (currentUrl.includes('/signup')) {
      let pattern = /\/ref\/[a-zA-Z0-9]/g;
      let refUrl = localStorage.getItem('ReferralURL');
      if (!pattern.test(refUrl)) {
        localStorage.setItem('ReferralURL', currentUrl);
      }
    }

    // Handle page names
    if (currentUrl.includes('/timifit')) {
      localStorage.setItem('pagename', 'fit-economy');
    }
    if (currentUrl.includes('/nls-rx-marketplace')) {
      localStorage.setItem('pagename', 'nls-rx-marketplace');
    }
    if (currentUrl.toLowerCase().includes('/consumer/summary')) {
      localStorage.setItem('pagename', 'consumerSummaryPage');
    }
    if (currentUrl.toLowerCase().includes('/consumer/faq')) {
      localStorage.setItem('pagename', 'consumerFaqNew');
    }

    // Handle popup window redirects
    try {
      if (window.opener && window.self.name === "popup") {
        var wparam = localStorage.getItem('wt19:awaydpset');
        if (wparam !== null && wparam !== undefined && wparam !== '') {
          let getlogin = window.location.href;
          if (getlogin.toLowerCase().includes("release-backoffice") || getlogin.toLowerCase().includes("dev-backoffice")) {
            window.opener.location.href = 'http://dev.glichealth.com/login?q=' + wparam;
            window.self.close();
          } else if (getlogin.toLowerCase().includes("localhost:")) {
            window.opener.location.href = 'http://localhost:5464/login?q=' + wparam;
            window.self.close();
          } else {
            window.opener.location.href = 'http://reporting.nulifespanrx.com/login?q=' + wparam;
            window.self.close();
          }
        }
      }
    } catch (error) {
      console.error('Popup window handling error:', error);
    }
  }, [location]);

  const handleCaching = () => {
    try {
      let version = localStorage.getItem('version');
      if (version !== packageJson.version) {
        if ('caches' in window) {
          caches.keys().then((names) => {
            names.forEach(name => {
              caches.delete(name);
            });
          });
          window.location.reload(true);
        }
        localStorage.removeItem('version');
        localStorage.setItem('version', packageJson.version);
      }
    } catch (err) {
      console.error('Caching error:', err);
    }
  };

  const checkUserDeactivation = () => {
    try {
      if (!userService.Check_Null_Empty(localStorage.getItem("ajs_user_id"))) {
        let Usermeta_Data = window.atob(localStorage.getItem('ajs_user_id'));
        checkDeactivateAccount(Usermeta_Data.split(':')[0], Usermeta_Data.split(':')[1]);
      }
    } catch (error) {
      console.error('User deactivation check error:', error);
    }
  };

  const checkDeactivateAccount = (UserId, UserName) => {
    try {
      userService.DeactivateAccount(UserId, UserName)
        .then(apidata => {
          try {
            if (apidata.length > 0) {
              if (apidata[0].userStatus === 'False') {
                userService.logout();
                window.location.reload();
              }
            }
          } catch (error) {
            console.error('Deactivation response error:', error);
          }
        });
    } catch (error) {
      console.error('Deactivation check error:', error);
    }
  };

  const checkValidateRefLink = (endPoint) => {
    try {
      userService.Validate_ReferralURL('', endPoint, '')
        .then(apidata => {
          try {
            if (apidata.length > 0) {
              if (apidata[0].code === '-1') {
                try {
                  localStorage.clear();
                  localStorage.removeItem('ajs_user_id');
                  localStorage.removeItem('ReferralURL');
                  localStorage.removeItem('_Host_Pro_Hckare_TKN');
                  localStorage.removeItem('_Secure_3PSID_DT');
                } catch (error) {
                  console.error('Cleanup error:', error);
                }
                try {
                  setTimeout(() => {
                    window.location.href = "http://mypagenotfound.com";
                  }, 10);
                } catch (err) {
                  console.error('Redirect error:', err);
                }
              }
            }
          } catch (error) {
            console.error('Referral validation response error:', error);
          }
        });
    } catch (error) {
      console.error('Referral validation error:', error);
    }
  };

  const GetDeploymentVersion = () => {
    try {
      userService.GetVersion('a', 'a')
        .then(apidata => {
          try {
            if (apidata.length > 0) {
              const currentVersion = localStorage.getItem("appVersion");
              if (currentVersion !== apidata[0].version) {
                localStorage.setItem("appVersion", apidata[0].version);
                window.location.reload();
              }
            }
          } catch (error) {
            console.error('Version check error:', error);
          }
        });
    } catch (error) {
      console.error('Version fetch error:', error);
    }
  };

  return (
    <HelmetProvider>
      <div>
        <Helmet>
          <title>NuLifeSpan Rx Backoffice</title>
          <meta name="viewport" content="width=device-width, initial-scale=1" shrink-to-fit="yes" />
        </Helmet>
      
      <Routes>
        <Route path="/" element={<Navigate to="/signup" replace />} />
        <Route path="/login" element={<LogIn />} />
        <Route path="/signup" element={<SignUp />} />
        <Route path="/signup/:linkSuffix" element={<SignUp />} />
        <Route path="/Account/Activate" element={<Activate_Account />} />
        <Route path="/passes/redirect" element={<PassRedirect />} />
        <Route path="/Account/SecActivate" element={<Activate_SecAccount />} />
        <Route path="/ref" element={<Affiliate_Url />} />
        <Route path="/affiliate" element={<Affiliate_Url />} />
        <Route path="/Summary" element={<SummaryPage />} />
        <Route path="/consumer/Summary" element={<ConsumerSummary />} />
        <Route path="/consumer/account" element={<ConsumerAccountInfo />} />
        <Route path="/consumer/share-card" element={<ConsumerShareCard />} />
        <Route path="/consumer/payment" element={<ConsumerWisePayment />} />
        <Route path="/consumerlogin" element={<ConsumerLoginPage />} />
        <Route path="/account" element={<Account_Info_page />} />
        <Route path="/cards" element={<OrderCardsPage />} />
        <Route path="/consumer/cards" element={<OrderCardsPage />} />
        <Route path="/consumer/faq" element={<ConsumerFaqPage />} />
        <Route path="/faq" element={<FaqPage />} />
        <Route path="/forgotpassword" element={<ForgetPassword />} />
        <Route path="/resetpassword" element={<ResetPassword />} />
        <Route path="/rusersetup" element={<RuserSetup />} />
        <Route path="/ousersetup" element={<OuserSetup />} />
        <Route path="/Admin/transactions" element={<Transactions />} />
        <Route path="/Admin/analytics/signups" element={<Analytics />} />
        <Route path="/Admin/analytics/geographic" element={<Geographic />} />
        <Route path="/Admin/analytics/pharmacies" element={<Pharmacies />} />
        <Route path="/dashboard" element={<UserTransactions />} />
        <Route path="/user/transactions" element={<UserTransactions />} />
        <Route path="/Admin/groups" element={<Groupno_Page />} />
        <Route path="/Admin/users" element={<User_Page />} />
        <Route path="/Admin/pricing" element={<PricingPage />} />
        <Route path="/Admin/referral" element={<Refferallink_Page />} />
        <Route path="/Admin/secondary-link" element={<Links_Page />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/compliance" element={<CompliancePage />} />
        <Route path="/fit-economy" element={<ComingSoon pageName="Fit Economy" />} />
        <Route path="/timifit" element={<ComingSoon pageName="Timifit" />} />
        <Route path="/nls-rx-marketplace" element={<Ecommerce />} />
        <Route path="/oauth_callback" element={<LogIn />} />
        <Route path="/Admin/report" element={<CommissionTracking />} />
        <Route path="/Admin/training" element={<AdminTraining />} />
        <Route path="/consumer/training" element={<ConsumerTraining />} />
        <Route path="/training" element={<UserTraining />} />
        <Route path="/page-not-found" element={<ErrorPage />} />
        <Route path="*" element={<Navigate to="/page-not-found" replace />} />
      </Routes>
    </div>
    </HelmetProvider>
  );
}

export default App;
